import SecureRequest from "utils/securerequest";

import type { AssessmentType } from "components/ClientDetails/Sections/Assessments/types";

/**
 * Maps each v3-supported assessment type to its API URL segment.
 * Vineland is excluded — it uses the legacy `/v2` endpoint.
 */
export const V3_ASSESSMENT_SEGMENTS: Partial<Record<AssessmentType, string>> = {
  cfql2: "cfql2",
  srs2: "srs2",
  qabf: "qabf",
  cpfq: "cpfq",
  vbmappMilestones: "vbmapp-milestones",
  vbmappBarriers: "vbmapp-barriers",
  vbmappTransition: "vbmapp-transition",
  peak: "peak",
  afls: "afls",
  efl: "essential-for-living",
  sociallySavvy: "socially-savvy",
};

/**
 * Fetches all assessments of a given type for a client from the v3 assessments API.
 *
 * @param clientId - The client's unique identifier
 * @param segment - The URL segment for the assessment type (e.g., "cfql2", "srs2")
 * @returns Axios response containing an array of assessment objects
 */
export async function getV3ClientAssessments(clientId: string, segment: string) {
  const sr = new SecureRequest();
  const Request = sr.getRequest();
  return Request.get(`/v3/assessments/clients/${clientId}/${segment}`);
}

/**
 * Creates a v3 assessment record for a client.
 *
 * @param clientId - The client's unique identifier
 * @param segment - The URL segment for the assessment type
 * @param payload - Assessment payload in API camelCase format
 */
export async function createV3ClientAssessment(
  clientId: string,
  segment: string,
  payload: Record<string, unknown>
) {
  const sr = new SecureRequest();
  const Request = sr.getRequest();
  return Request.post(`/v3/assessments/clients/${clientId}/${segment}`, payload);
}

/**
 * Creates a v3 assessment by assessment type.
 *
 * @param clientId - The client's unique identifier
 * @param assessmentType - UI assessment type identifier
 * @param payload - Assessment payload in API camelCase format
 */
export async function createV3ClientAssessmentByType(
  clientId: string,
  assessmentType: AssessmentType,
  payload: Record<string, unknown>
) {
  const segment = V3_ASSESSMENT_SEGMENTS[assessmentType];
  if (!segment) {
    throw new Error(`Unsupported v3 assessment type: ${assessmentType}`);
  }

  return createV3ClientAssessment(clientId, segment, payload);
}

/**
 * Partially updates a v3 assessment record.
 *
 * @param assessmentType - UI assessment type identifier
 * @param assessmentId - The assessment's unique identifier
 * @param payload - Fields to patch in API camelCase format
 */
export async function patchV3ClientAssessment(
  assessmentType: AssessmentType,
  assessmentId: string | number,
  payload: Record<string, unknown>
) {
  const segment = V3_ASSESSMENT_SEGMENTS[assessmentType];
  if (!segment) {
    throw new Error(`Unsupported v3 assessment type: ${assessmentType}`);
  }

  const sr = new SecureRequest();
  const Request = sr.getRequest();
  return Request.patch(`/v3/assessments/${segment}/${assessmentId}`, payload);
}
